import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Heading from '@atlaskit/heading';
import { Label } from '@atlaskit/form';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { TEMPLATE_KEY as PRIORITIZATION_KEY } from '@atlassian/jira-project-template-select-v2/src/utils/templates/product-discovery/prioritization/index.tsx';
import { useCanProductUserUpgradeAndPay } from '@atlassian/jira-billing/src/controllers/billing-permissions/index.tsx';
import {
	ContextualAnalyticsData,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useFlagsService } from '@atlassian/jira-flags';
import type { CreateProjectResponse } from '@atlassian/jira-project-create-common/src/services/create-project/types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import onboardingCommonMessages from '../../common/messages.tsx';
import { CreateFormOnboardingGrid } from '../../common/ui/create-form-container/create-form-onboarding-grid/index.tsx';
import { OnboardingContent } from '../../common/ui/content/index.tsx';
import { LegalNotice } from '../../common/ui/content/legal-notice/index.tsx';
import { TabContent } from '../../common/ui/tab-content/index.tsx';

import { ControlItem } from '../../common/ui/onboarding-grid/content-layout-section/controls/control-item/index.tsx';
import { OnboardingModalContextProvider } from '../../controllers/onboarding-modal-context/index.tsx';
import { getTemplateContentFromKey } from '../../utils/get-template-content-from-key/index.tsx';
import { CreateFormOnboardingContainer } from '../../common/ui/create-form-container/index.tsx';
import backgroundLight from '../../common/assets/create-form-background/create-form-background-light.png';
import backgroundDark from '../../common/assets/create-form-background/create-form-background-dark.png';
import { useAutoProvisionedOnboardingContentDismissal } from '../../utils/use-auto-provisioned-onboarding-content-dismissal/index.tsx';
import { getProductTypeKeyFromTargetProduct } from '../../utils/get-product-type-key-from-target-product/index.tsx';
import { useProjectCreationHandler } from '../../utils/use-project-creation-handler/index.tsx';
import type { ProjectTemplateOnboardingContentProps } from './types.tsx';

export const ProjectTemplateOnboardingContent = ({
	targetProduct,
	sourceComponent,
	templateKey = PRIORITIZATION_KEY,
	onClose,
}: ProjectTemplateOnboardingContentProps): React.JSX.Element => {
	const { push } = useRouterActions();
	const { showFlag } = useFlagsService();
	const { formatMessage } = useIntl();
	const [selectedFeature, setSelectedFeature] = useState(0);
	const [hasConsented, setHasConsented] = useState(false);
	const hasDismissedRef = useRef(false);

	const { dismiss: dismissOnboardingContentForFutureRenders } =
		useAutoProvisionedOnboardingContentDismissal(getProductTypeKeyFromTargetProduct(targetProduct));

	const handleFeatureUpdate = useCallback(
		(index: number) => setSelectedFeature(index),
		[setSelectedFeature],
	);
	const { hasPaymentDetailsOnFile, isLoading: isLoadingBillingPermissions } =
		useCanProductUserUpgradeAndPay({
			skip: false,
			productKey: 'jira-software.ondemand', // use source product as product key to find if has payment details
		});

	const templateContent = useMemo(() => {
		return getTemplateContentFromKey(templateKey);
	}, [templateKey]);

	const [projectTitle, setProjectTitle] = useState(
		templateContent?.projectNameDefault && formatMessage(templateContent.projectNameDefault),
	);

	const handleLegalNoticeChange = useCallback(
		(value: boolean) => {
			setHasConsented(value);
		},
		[setHasConsented],
	);

	const onSuccess = useCallback(
		(
			createProjectResponse: CreateProjectResponse,
			e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
			analyticsEvent: UIAnalyticsEvent,
		) => {
			push(createProjectResponse.returnUrl);

			onClose(e, analyticsEvent);
		},
		[onClose, push],
	);

	const onFail = useCallback(
		(_error: Error) => {
			showFlag({
				type: 'error',
				id: 'project-creation-failure',
				title: formatMessage(
					expVal('project-terminology-refresh', 'isEnabled', false)
						? onboardingCommonMessages.createProjectFailureTitleNonFinalGalaxia
						: onboardingCommonMessages.createProjectFailureTitleNonFinal,
				),
				description: formatMessage(
					onboardingCommonMessages.createProjectFailureDescriptionNonFinal,
				),
				messageId:
					'cross-flow-onboarding-modal.ui.project-template-onboarding-content.show-flag.error',
				messageType: 'transactional',
			});
		},
		[formatMessage, showFlag],
	);

	const {
		handleProjectCreation,
		isCreating,
		isUpgrading,
		isDisabled,
		targetEdition,
		shouldUpgrade,
	} = useProjectCreationHandler({
		templateKey,
		templateContent,
		targetProduct,
		hasPaymentDetails: hasPaymentDetailsOnFile || false,
		onSuccess,
		onFail,
		sourceComponent,
	});

	useEffect(() => {
		if (!hasDismissedRef.current) {
			hasDismissedRef.current = true;
			dismissOnboardingContentForFutureRenders();
		}
	}, [dismissOnboardingContentForFutureRenders, hasDismissedRef]);

	return (
		<ContextualAnalyticsData
			attributes={{
				featureItemIndex: selectedFeature,
				templateKey,
				message: templateContent.features[selectedFeature].description.defaultMessage,
			}}
		>
			<OnboardingModalContextProvider targetProduct={targetProduct} open>
				<CreateFormOnboardingContainer
					targetProduct={targetProduct}
					sourceComponent={sourceComponent}
					ariaLabel={formatMessage(templateContent.ariaLabel)}
				>
					<CreateFormOnboardingGrid enableCrossButton={false}>
						<CreateFormOnboardingGrid.ContentSection>
							<OnboardingContent space="space.200" inModal={false}>
								<Heading size="large">{formatMessage(templateContent.modalTitleDefault)}</Heading>
								<Text size="medium">{formatMessage(templateContent.projectDescription)}</Text>
								<Box>
									<Label htmlFor="onboarding-modal-project-title">
										{formatMessage(templateContent.projectNameLabel)}
									</Label>
									<Textfield
										placeholder={formatMessage(templateContent.projectNamePlaceholder)}
										id="onboarding-modal-project-title"
										maxLength={50}
										value={projectTitle}
										required
										aria-required="true"
										onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
											setProjectTitle(e.target.value)
										}
									/>
								</Box>
								{isLoadingBillingPermissions ? (
									<>
										<Skeleton width="100%" height={token('space.075')} />
										<Skeleton width="90%" height={token('space.075')} />
										<Skeleton width="50%" height={token('space.075')} />
									</>
								) : (
									<LegalNotice
										hasPaymentDetails={hasPaymentDetailsOnFile}
										onChange={handleLegalNoticeChange}
										edition={targetEdition || 'STANDARD_EDITION'}
										shouldUpgrade={shouldUpgrade}
									/>
								)}
							</OnboardingContent>
							<CreateFormOnboardingGrid.Controls>
								<ControlItem variant="cancel" onClick={onClose} />
								<ControlItem
									variant="get-started"
									appearance="primary"
									isLoading={isCreating || isUpgrading}
									disabled={
										!projectTitle ||
										projectTitle.trim() === '' ||
										(targetEdition !== 'FREE_EDITION' &&
											targetEdition !== 'UNLICENSED' &&
											!hasConsented &&
											!!hasPaymentDetailsOnFile &&
											shouldUpgrade) ||
										isDisabled
									}
									onClick={handleProjectCreation}
								/>
							</CreateFormOnboardingGrid.Controls>
						</CreateFormOnboardingGrid.ContentSection>
						<CreateFormOnboardingGrid.ImageSection
							backgroundLight={backgroundLight}
							backgroundDark={backgroundDark}
						>
							<TabContent
								onChange={handleFeatureUpdate}
								selected={selectedFeature}
								content={templateContent}
								templateKey={templateKey}
								dynamicTemplateName={projectTitle}
								inModal={false}
							/>
						</CreateFormOnboardingGrid.ImageSection>
					</CreateFormOnboardingGrid>
				</CreateFormOnboardingContainer>
			</OnboardingModalContextProvider>
		</ContextualAnalyticsData>
	);
};
